import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { useDataContext } from '../../contexts/DataContext';
import Patient from '../../models/Patient';
import { FilesService } from '../../Utils/Files.service';
import { ConfirmMassPatientUpload } from './components/ConfirmMassPatientUpload';
import { useDownloadMassPatientLoadTemplate } from './hooks/Patient.hooks';
import {
    modalPatientMassUploadConfirmationConfig,
    spinnerPatientStyles,
} from './models/Patient.models';
import { PatientMassLoadService } from './services/PatientMassLoad.service';
import './styles/PatientForm.styles.scss';

type Props = {
    id: string | undefined;
    patient: Patient;
    setPatient: Dispatch<SetStateAction<Patient>>;
    setCompleted: Dispatch<SetStateAction<boolean>>;
};

/**
 * Subcomponent of PatientMaster used to enter patient data.
 */
const PatientForm = ({ id, patient, setPatient, setCompleted }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // const [username] = useState('');
    // const [password] = useState('');

    // const { setModalMessage } = useModalContext();
    const { currentUserRole } = useDataContext();

    // Set patient properties based on changes in form text fields.
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target;
        setPatient({ ...patient, [id]: value !== '' ? +value : null });
    };

    // Se ha eliminado el sexo
    // Set patient Sex based on changes in form radio buttons.
    // const handleSexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     const { id, name, checked } = e.target;
    //     if (!checked) return;
    //     setPatient({ ...patient, [name]: parseBoolean(id.split('-')[1]) });
    // };

    // Set patient CDR based on changes in form radio buttons.
    const handleCdrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, checked } = e.target;
        if (!checked) return;
        setPatient({ ...patient, cdr: +id.split('-')[1] });
    };

    const handleSobChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target;
        const sob = parseFloat(value.replaceAll(',', '.'));
        if (isNaN(sob)) {
            setPatient({ ...patient, [id]: null });
        } else {
            setPatient({ ...patient, [id]: Math.round(+value * 10) / 10 });
        }
    };

    const isEmptyAge = patient.age === undefined || patient.age === null;
    let isAgeOutOfRange = false;
    if (patient.age) {
        isAgeOutOfRange = patient.age < 55 || patient?.age > 85;
    }

    const isEmptyCDRSOB = patient.sob === undefined || patient.sob === null;
    let isCDRSOBOutOfRange = false;
    if (patient.sob) {
        isCDRSOBOutOfRange = patient.sob < 0 || patient?.sob > 18;
    }

    // Set patient ApoE based on changes in form radio buttons.
    const handleApoeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, checked } = e.target;
        const idMapped = id === 'add_later' ? 'add_later' : id.split('-')[1];
        if (!checked) return;
        setPatient({ ...patient, apoe: idMapped });
    };

    //Se ha eliminado el Amyloid PET
    // Set patient Amyloid-PET based on changes in form radio buttons.
    // const handleAmyloidPetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     const { id, name, checked } = e.target;
    //     if (!checked) return;
    //     setPatient({ ...patient, [name]: parseBoolean(id.split('-')[1]) });
    // };

    // Se ha eliminado el rol neurologo
    // Set patient neurological conclusion based on changes in combobox.
    // const handleNeuroConclusionChange = (option: number) => {
    //     setPatient({ ...patient, neuroEval: option });
    // };

    // Inform that patient data introduction has been completed.
    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        //Habilitamos los valores 0 y 0.5 para CDR
        // if (currentUserRole === 'client' && patient.cdr !== 0.5) {
        //     setModalMessage(t('feedback.cdr_must_be_05'));
        //     return;
        // }

        //Se ha eliminado el rol neurologo
        // if (currentUserRole === 'neuro') {
        //     if (!patient.neuroEval) {
        //         setModalMessage(t('feedback.enter_conclusion'));
        //         return;
        //     }

        //     const loginResponse = await authenticate({ username, password });
        //     if (!loginResponse) {
        //         setModalMessage(t('feedback.sign_report_auth_failed'));
        //         return;
        //     }
        // }

        setCompleted(true);
    };

    const apoes = ['E2.E2', 'E2.E3', 'E2.E4', 'E3.E3', 'E3.E4', 'E4.E4', 'add_later'];
    const cdrs = [0, 0.5];

    // const disabled = currentUserRole === 'neuro' ? 'disabled' : undefined; // Se ha eliminado el rol neurologo

    const cdrError = patient.cdr === -1 ? t('label.req_info') : null;

    //#region Mass patient load

    //services
    const patientMassLoadService = new PatientMassLoadService();
    const filesService = new FilesService();

    //template
    const downloadMassPatientLoadTemplate = useDownloadMassPatientLoadTemplate();

    //upload
    const [showConfirmMassPatientUploadModal, setShowConfirmMassPatientUploadModal] =
        useState(false);
    const [patientsFile, setPatientsFile] = useState(null as File | null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [loadingSpinner, setLoadingSpinner] = useState(false);
    const { currentUserId } = useDataContext();

    const handleImportClick = () => {
        // Dispara el input oculto
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        //Get the file
        const file = event.target.files?.[0];
        if (!file) return;

        //Save the file in the component state
        setPatientsFile(file);
        setLoadingSpinner(true);

        //Validate the file against backend
        try {
            const result = await patientMassLoadService.validatePatientsMassUploadExcel(
                file,
                currentUserId
            );

            if (result.success === true) {
                // Show confirmation modal
                setShowConfirmMassPatientUploadModal(true);
                // Download error txt and show notification
            } else if (result.success === false && result.message) {
                filesService.downloadTxtFromBase64(
                    result.message,
                    `${t('error.validation_errors_mass_upload_txt_name')}.txt`
                );

                toast.error(t('error.validation_errors_mass_upload_message'));
            }
        } catch (err: unknown) {
            toast.error(t('error.validation_errors_mass_upload_message'));
        } finally {
            setLoadingSpinner(false);
        }

        // Limpiar input para poder seleccionar el mismo archivo otra vez si hace falta
        event.target.value = '';
    };

    const handleMassUploadConfirm = async () => {
        //Upload patients excel file
        try {
            if (!patientsFile) return;

            setLoadingSpinner(true);

            const result = await patientMassLoadService.loadPatientsMassUploadExcel(
                patientsFile,
                currentUserId
            );

            if (result.success === true) {
                //Download txt with patient ids
                filesService.downloadTxtFromBase64(
                    result.message,
                    `${t('mass_upload.upload_completed_file_name')}.txt`
                );
                toast.success(t('mass_upload.upload_completed'));
                // Download error txt and show notification
            } else if (result.success === false && result.message) {
                filesService.downloadTxtFromBase64(
                    result.message,
                    `${t('error.uploading_errors_mass_upload_txt_name')}.txt`
                );

                toast.error(t('error.uploading_errors_mass_upload_message'));
            }
        } catch (err: unknown) {
            toast.error(t('error.uploading_errors_mass_upload_txt_name'));
        } finally {
            setLoadingSpinner(false);
        }
    };

    //upload confirmation
    Modal.setAppElement('#root');

    //#endregion

    return (
        <section className="patient-form">
            <div className="single-patient">
                <h1>{id ? t('label.edit_patient') : t('label.new_patient')}</h1>
                <form onSubmit={onSubmit}>
                    <div style={{ display: 'none' }}>{patient.id}</div>

                    <div className="form-box">
                        {/* Code */}
                        {patient.code && (
                            <>
                                <label htmlFor="code">{t('label.code')}</label>
                                <input
                                    type="text"
                                    id="code"
                                    name="code"
                                    disabled={true}
                                    value={patient.code}
                                />
                            </>
                        )}

                        {/* Sex */}
                        {/* <p>{t('label.sex')}</p>
                <div>
                    <fieldset>
                        <legend>{t('label.sex')}</legend>
                        <label
                            htmlFor="sex-false"
                            className={disabled}
                        >
                            {t('label.female')}
                        </label>
                        <input
                            type="radio"
                            id="sex-false"
                            name="sex"
                            checked={patient.sex === false}
                            onChange={handleSexChange}
                            disabled={currentUserRole === 'neuro'}
                            required
                        />
                        <label
                            htmlFor="sex-true"
                            className={disabled}
                        >
                            {t('label.male')}
                        </label>
                        <input
                            type="radio"
                            id="sex-true"
                            name="sex"
                            checked={patient.sex === true}
                            onChange={handleSexChange}
                            disabled={currentUserRole === 'neuro'}
                            required
                        />
                    </fieldset>
                    <span className={patient.sex === undefined ? 'validation' : 'validated'}>
                        {t('label.req_info')}
                    </span>
                </div> */}

                        {/* Age */}
                        <label htmlFor="age">{t('label.age')}</label>
                        <div>
                            <input
                                type="number"
                                id="age"
                                name="age"
                                min="55"
                                max="85"
                                value={patient.age ?? ''}
                                onChange={handleChange}
                                disabled={currentUserRole === 'admin'}
                                required
                            />
                            {/* Mostrar "Información necesaria" al iniciar el formulario */}
                            <span
                                className="empty-necessary"
                                style={{ display: isEmptyAge ? 'inline' : 'none' }}
                            >
                                {t('label.req_info')}
                            </span>
                            {/* Mostrar error si el valor no se encuentra entre 0 y 18 */}
                            <span
                                className="validation"
                                style={{ display: isAgeOutOfRange ? 'inline' : 'none' }}
                            >
                                {t('label.must_be_55_85')}
                            </span>
                        </div>

                        {/* CDR */}
                        <p>CDR</p>
                        <div>
                            <fieldset>
                                <legend>CDR</legend>
                                {cdrs.map((cdr) => (
                                    <span key={cdr}>
                                        <label htmlFor={`cdr-${cdr}`}>{cdr}</label>
                                        <input
                                            type="radio"
                                            id={`cdr-${cdr}`}
                                            name="cdr"
                                            checked={patient.cdr === cdr}
                                            onChange={handleCdrChange}
                                            disabled={currentUserRole === 'admin'}
                                            required
                                        />
                                    </span>
                                ))}
                            </fieldset>
                            <span className={patient.cdr !== null ? 'validation' : 'validated'}>
                                {cdrError}
                            </span>
                        </div>

                        {/* SOB */}
                        <label htmlFor="sob">CDR-SOB</label>
                        <div>
                            <input
                                type="number"
                                id="sob"
                                name="sob"
                                step="0.1"
                                min="0"
                                max="18"
                                value={
                                    patient.sob === null || patient.sob === undefined
                                        ? ''
                                        : patient.sob
                                }
                                onChange={handleSobChange}
                                disabled={currentUserRole === 'admin'}
                                required
                            />

                            {/* Mostrar "Información necesaria" al iniciar el formulario */}
                            <span
                                className="empty-necessary"
                                style={{ display: isEmptyCDRSOB ? 'inline' : 'none' }}
                            >
                                {t('label.req_info')}
                            </span>
                            {/* Mostrar error si el valor no se encuentra entre 0 y 18 */}
                            <span
                                className="validation"
                                style={{ display: isCDRSOBOutOfRange ? 'inline' : 'none' }}
                            >
                                {t('label.must_be_0_18')}
                            </span>
                        </div>

                        {/* MMSE */}
                        {/* <label htmlFor="mmse">MMSE</label>
                <div>
                    <input
                        type="number"
                        id="mmse"
                        name="mmse"
                        min="0"
                        max="30"
                        value={patient.mmse ?? ''}
                        onChange={handleChange}
                        disabled={currentUserRole === 'neuro'}
                    />
                    <span className="validation">{t('label.must_be_0_30')}</span>
                </div> */}

                        {/* ApoE */}
                        <p>{t('label.apoe_genotype')}</p>
                        <div>
                            <fieldset>
                                <legend>{t('label.apoe_genotype')}</legend>
                                {apoes.map((apoe) => (
                                    <span key={apoe ?? 'add-later'}>
                                        <label
                                            htmlFor={
                                                apoe === 'add_later' ? 'add_later' : `apoe-${apoe}`
                                            }
                                        >
                                            {apoe === 'add_later' ? t('label.add_later') : apoe}
                                        </label>
                                        <input
                                            type="radio"
                                            id={apoe === 'add_later' ? 'add_later' : `apoe-${apoe}`}
                                            name="apoe"
                                            checked={patient.apoe === apoe}
                                            onChange={handleApoeChange}
                                            disabled={currentUserRole === 'neuro'}
                                            required
                                        />
                                    </span>
                                ))}
                            </fieldset>
                            <span className={patient.apoe ? 'validated' : 'validation'}>
                                {t('label.req_info')}
                            </span>
                        </div>

                        {/* Amyloid PET */}
                        {/* <p>{t('label.amyloid_pet')}</p>
                <div>
                    <fieldset>
                        <legend>Amyloid PET</legend>
                        <label
                            htmlFor="amyloidPet-false"
                            className={disabled}
                        >
                            {t('label.negative')}
                        </label>
                        <input
                            type="radio"
                            id="amyloidPet-false"
                            name="amyloidPet"
                            value="false"
                            required
                            checked={patient.amyloidPet === false}
                            onChange={handleAmyloidPetChange}
                            disabled={currentUserRole === 'neuro'}
                        />
                        <label
                            htmlFor="amyloidPet-true"
                            className={disabled}
                        >
                            {t('label.positive')}
                        </label>
                        <input
                            type="radio"
                            id="amyloidPet-true"
                            name="amyloidPet"
                            value="false"
                            required
                            checked={patient.amyloidPet === true}
                            onChange={handleAmyloidPetChange}
                            disabled={currentUserRole === 'neuro'}
                        />
                        <label
                            htmlFor="amyloidPet-null"
                            className={disabled}
                        >
                            {t('label.not_available')}
                        </label>
                        <input
                            type="radio"
                            id="amyloidPet-null"
                            name="amyloidPet"
                            value="false"
                            required
                            checked={patient.amyloidPet === null}
                            onChange={handleAmyloidPetChange}
                            disabled={currentUserRole === 'neuro'}
                        />
                    </fieldset>
                </div> */}

                        {/* Output probabilities */}
                        {patient.probCtrl !== null && (
                            <>
                                <label htmlFor="probCtrl">{t('label.prob_ctrl')}</label>
                                <input
                                    type="text"
                                    id="probCtrl"
                                    name="probCtrl"
                                    disabled={true}
                                    value={`${((patient.probCtrl ?? 0) * 100).toFixed(1)} %`}
                                />
                            </>
                        )}
                        {patient.probMci !== null && (
                            <>
                                <label htmlFor="probMci">{t('label.prob_mci')}</label>
                                <input
                                    type="text"
                                    id="probMci"
                                    name="probMci"
                                    disabled={true}
                                    value={`${((patient.probMci ?? 0) * 100).toFixed(1)} %`}
                                />
                            </>
                        )}
                        {patient.probAd !== null && (
                            <>
                                <label htmlFor="probAd">{t('label.prob_ad')}</label>
                                <input
                                    type="text"
                                    id="probAd"
                                    name="probAd"
                                    disabled={true}
                                    value={`${((patient.probAd ?? 0) * 100).toFixed(1)} %`}
                                />
                            </>
                        )}

                        {/* Eliminando rol neurólogo */}
                        {/* Neurologist's evaluation */}
                        {/* {currentUserRole === 'neuro' && (
                    <>
                        <PatientNeuroConclusion onSelected={handleNeuroConclusionChange} />
                        <label htmlFor="username">{t('label.username_required_sign_report')}</label>
                        <input
                            type="text"
                            id="username"
                            autoComplete="false"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                        <label htmlFor="password">{t('label.password_required_sign_report')}</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </>
                )} */}
                        <div></div>
                        <div>
                            <button type="submit">{t('button.save_changes')}</button>
                            <button
                                type="button"
                                onClick={() => navigate('/patient/list')}
                            >
                                {t('button.back_to_list')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            {/* Mass patient upload */}
            {!id && (
                <div className="mass-upload">
                    <h1>{t('label.mass_upload')}</h1>
                    <button
                        type="button"
                        onClick={() => downloadMassPatientLoadTemplate()}
                    >
                        {t('label.download_template')}
                    </button>
                    <button
                        type="button"
                        onClick={handleImportClick}
                    >
                        {t('label.import_patients')}
                    </button>

                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept=".xlsx"
                        onChange={handleFileChange}
                    />
                </div>
            )}
            <Modal
                isOpen={loadingSpinner || showConfirmMassPatientUploadModal}
                style={modalPatientMassUploadConfirmationConfig}
                contentLabel="Mass Patient Upload"
                onRequestClose={() => {
                    if (showConfirmMassPatientUploadModal)
                        setShowConfirmMassPatientUploadModal(false);
                }}
            >
                {loadingSpinner ? (
                    <ClipLoader
                        color="#1a365d"
                        loading={loadingSpinner}
                        cssOverride={spinnerPatientStyles}
                        size={120}
                        aria-label="Loading mass patient upload spinner"
                    />
                ) : (
                    <ConfirmMassPatientUpload
                        onClose={() => setShowConfirmMassPatientUploadModal(false)}
                        onConfirm={handleMassUploadConfirm}
                    />
                )}
            </Modal>
        </section>
    );
};

export default PatientForm;
